import { Link } from "react-router-dom";
import { useKatlasStatus, KatlasModeChip, HowKatlasWorksButton } from "../components/Katlas";
import { EvidenceMatrix } from "../components/EvidenceMatrix";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Sparkles, ShieldCheck, Wallet, UserRound, FileCheck2, Network,
  ArrowDown, ArrowRight, Check, X, Lock, Info, Layers
} from "lucide-react";

/* ----- KATLAS capability explainer — separate from the main walkthrough.
   Reachable from the header "KATLAS capability" button and route /katlas-capability.
----- */

function Chapter({ num, title, kicker, children }) {
  return (
    <Card className="p-6 sm:p-7 border-border race-card space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Chapter {num}</div>
          <h2 className="text-xl sm:text-2xl font-semibold mt-1 leading-tight">{title}</h2>
          {kicker && <div className="text-[12px] text-muted-foreground font-mono-ui mt-1">{kicker}</div>}
        </div>
      </div>
      <div className="space-y-3 text-sm leading-relaxed">{children}</div>
    </Card>
  );
}

function Bullet({ children, icon: Icon = Check }) {
  return (
    <li className="flex gap-2 text-[13px]">
      <Icon className="h-3.5 w-3.5 mt-0.5 text-[var(--rag-green-text)] shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function FlowStep({ icon: Icon, label, accent = false, mono }) {
  return (
    <div className={`rounded-md border ${accent ? "border-[#F25E1D] bg-[#FFF7F2]" : "border-border bg-white"} px-3 py-2 inline-flex items-center gap-2 text-[12px]`}>
      <Icon className={`h-3.5 w-3.5 ${accent ? "text-[#F25E1D]" : "text-muted-foreground"}`} />
      <span>{label}</span>
      {mono && <span className="font-mono-ui text-[10px] text-muted-foreground ml-1">{mono}</span>}
    </div>
  );
}

export default function KatlasCapability() {
  const status = useKatlasStatus();
  const live = status?.status === "active";

  return (
    <div className="space-y-8" data-testid="katlas-capability-page">
      {/* Hero */}
      <section className="rounded-2xl border border-border bg-white p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="signal-bar" />
          <span className="tracking-eyebrow text-muted-foreground">KATLAS capability · Operator explainer</span>
          <KatlasModeChip size="lg" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
          The app runs the workflow.<br className="hidden sm:block" />
          <span className="text-[#F25E1D]">KATLAS attests the governed action.</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-3 max-w-3xl leading-relaxed">
          This page is separate from the customer walkthrough. It is for technical and governance
          reviewers who need to see clearly what the application is doing, what AI is doing, and what
          KATLAS is doing — and what changes the moment a live node is connected.
        </p>
        <p className="text-[13px] mt-3 max-w-3xl leading-relaxed border-l-2 border-[#F25E1D] pl-3 italic">
          “AI suggests. Wallets prove. KATLAS governs. Officers approve. The platform keeps the receipt.”
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Link to="/"><Button variant="outline" size="sm">Back to landing</Button></Link>
          <HowKatlasWorksButton variant="ghost" size="sm" />
        </div>
      </section>

      {/* Critical positioning */}
      <Card className="p-5 border-border bg-[#FAFAFB] race-card" data-testid="positioning-block">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-[#F25E1D] shrink-0 mt-0.5" />
          <div className="text-sm leading-relaxed space-y-2">
            <p>
              <b>Why this matters.</b> Do not read this POC as a scripted workflow with a decorative hash.
            </p>
            <p>
              <b>ClubCierge is connected to the live KATLAS node/API.</b> When an authorised action is approved,
              KATLAS signs a receipt showing who approved what, when, what evidence was shared, and what was withheld.
            </p>
            <p>
              The visible workflow remains a demonstration environment, but the governance pattern is live:
              custody is represented through controlled profiles, authority is enforced through role and policy
              gates, and receipts are signed through the KATLAS node.
            </p>
          </div>
        </div>
      </Card>

      {/* Four chapters */}
      <Chapter num={1} title="What the ClubCierge app does" kicker="Application surface · club-facing workflow">
        <p>
          ClubCierge shows how an existing club website can be enhanced with AI-assisted workflows,
          member-controlled profiles, bounded permissions, human approvals and evidence-backed receipts.
        </p>
        <ul className="space-y-1.5">
          <Bullet icon={ArrowRight}>Marcus Reid driver profile — performance, media, funding, sponsor readiness</Bullet>
          <Bullet icon={ArrowRight}>Pole Position sponsor profile — verified credentials, aims, terms</Bullet>
          <Bullet icon={ArrowRight}>Introduction Review Inbox — proposed matches with AI alignment</Bullet>
          <Bullet icon={ArrowRight}>Eligible support &amp; kit — bounded shop/supplier offers with parent + officer gates</Bullet>
          <Bullet icon={ArrowRight}>Receipts — what was checked, shared, withheld, approved</Bullet>
        </ul>
        <div className="text-[12px] text-muted-foreground border-t border-border pt-3 italic">
          This is the club-facing application layer. It is the visible surface — not the governance layer.
        </div>
      </Chapter>

      <Chapter num={2} title="What AI does and does not do" kicker="Bounded assistant · never the decider">
        <p>
          AI helps structure information, suggest alignment and flag missing evidence. It does not approve
          introductions, override permissions, access private wallet contents or make safeguarding-sensitive
          decisions.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-md border border-border p-3 bg-white">
            <div className="tracking-eyebrow text-muted-foreground">AI does</div>
            <ul className="text-[12px] mt-1.5 space-y-1">
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Suggest alignment between a driver and a sponsor</li>
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Structure messy evidence into a comparable view</li>
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Flag missing or weak items</li>
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Explain why an offer might fit a driver's profile</li>
            </ul>
          </div>
          <div className="rounded-md border border-border p-3 bg-[#FBE9E7]/30">
            <div className="tracking-eyebrow text-muted-foreground">AI does not</div>
            <ul className="text-[12px] mt-1.5 space-y-1">
              <li className="flex gap-1.5"><X className="h-3 w-3 mt-0.5 text-[var(--rag-red-text)]" />Approve introductions or claims</li>
              <li className="flex gap-1.5"><X className="h-3 w-3 mt-0.5 text-[var(--rag-red-text)]" />Override parent / officer gates</li>
              <li className="flex gap-1.5"><X className="h-3 w-3 mt-0.5 text-[var(--rag-red-text)]" />Access private wallet contents</li>
              <li className="flex gap-1.5"><X className="h-3 w-3 mt-0.5 text-[var(--rag-red-text)]" />Make safeguarding-sensitive decisions</li>
            </ul>
          </div>
        </div>
        <div className="text-[12px] text-muted-foreground border-t border-border pt-3 italic">AI suggests. It does not decide.</div>
      </Chapter>

      <Chapter num={3} title="What KATLAS does" kicker="Governance layer · attestation, not narrative">
        <p>
          KATLAS governs the operational step. It checks the role and permission context, records what was
          approved, what was shared, what was withheld, and creates or receives a receipt / attestation for
          the action.
        </p>
        <div className="rounded-md border border-border p-3 bg-white">
          <div className="tracking-eyebrow text-muted-foreground">A live KATLAS node can attest</div>
          <ul className="text-[12px] mt-1.5 space-y-1">
            <li className="flex gap-1.5"><ShieldCheck className="h-3 w-3 mt-0.5 text-[#F25E1D]" />Action type — sponsor introduction approved, offer claim approved, mitigation accepted</li>
            <li className="flex gap-1.5"><ShieldCheck className="h-3 w-3 mt-0.5 text-[#F25E1D]" />Approving role — Club Secretary, Parent / Guardian, Safeguarding Lead, Maintenance Lead</li>
            <li className="flex gap-1.5"><ShieldCheck className="h-3 w-3 mt-0.5 text-[#F25E1D]" />Policy gate result — passed, blocked, approval required</li>
            <li className="flex gap-1.5"><ShieldCheck className="h-3 w-3 mt-0.5 text-[#F25E1D]" />Permission state — what was shared, what was withheld</li>
            <li className="flex gap-1.5"><ShieldCheck className="h-3 w-3 mt-0.5 text-[#F25E1D]" />Evidence references — which records were relied on (not the records themselves)</li>
            <li className="flex gap-1.5"><ShieldCheck className="h-3 w-3 mt-0.5 text-[#F25E1D]" />Timestamp and receipt reference</li>
            <li className="flex gap-1.5"><ShieldCheck className="h-3 w-3 mt-0.5 text-[#F25E1D]" />Node signature / attestation id where available</li>
          </ul>
        </div>
        <div className="rounded-md border border-dashed border-[#F25E1D]/40 p-3 bg-[#FFF7F2]">
          <div className="tracking-eyebrow text-muted-foreground inline-flex items-center gap-1"><Lock className="h-3 w-3" /> What the node does not need</div>
          <ul className="text-[12px] mt-1.5 space-y-0.5 text-muted-foreground">
            <li>· Full date of birth · Family notes · Safeguarding records · Private contact details</li>
            <li>· Withheld media assets · Unapproved documents · Payment details · Unrestricted AI prompt context</li>
          </ul>
          <div className="text-[11px] mt-2 italic">KATLAS does not need raw personal data to govern the action. It can attest the approval event using metadata, roles, permissions and evidence references.</div>
        </div>
        <div className="text-[12px] text-muted-foreground border-t border-border pt-3 italic">KATLAS is the governance layer — not the AI model and not the user interface.</div>
      </Chapter>

      <Chapter num={4} title="Simulation today, live node in production" kicker="The honest difference">
        <p>
          This demo currently shows the KATLAS governance step as a {live ? "live node attestation" : "simulation"} so the use case is self-contained. KATLAS can also be connected as a live node / API. In live
          mode, the ClubCierge backend sends a minimal governance payload to the KATLAS node, the node attests
          the action, and the receipt shows a node attestation id.
        </p>
        <div className="grid md:grid-cols-2 gap-3" data-testid="modes-grid">
          <div className="rounded-md border border-border p-4 bg-white">
            <div className="tracking-eyebrow text-muted-foreground">Current POC</div>
            <ul className="text-[12px] mt-2 space-y-1">
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />KATLAS governance simulation</li>
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Local policy gate</li>
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Local receipt hash</li>
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Visible operating model</li>
            </ul>
          </div>
          <div className="rounded-md border-2 border-[#F25E1D]/50 p-4 bg-[#FFF7F2]">
            <div className="tracking-eyebrow text-[#F25E1D] inline-flex items-center gap-1"><Network className="h-3 w-3" />Live KATLAS node</div>
            <ul className="text-[12px] mt-2 space-y-1">
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />KATLAS node active</li>
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Node receives minimal governance payload</li>
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Node returns attestation id / hash / timestamp</li>
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Receipt shows node attestation</li>
              <li className="flex gap-1.5"><Check className="h-3 w-3 mt-0.5 text-[var(--rag-green-text)]" />Raw private data and withheld assets are not sent</li>
            </ul>
          </div>
        </div>
        <div className="text-[12px] text-muted-foreground border-t border-border pt-3 italic">The simulation demonstrates the workflow. The live node proves participation.</div>
        <div className="text-[12px] text-muted-foreground leading-relaxed">
          <b>Integration status:</b> ClubCierge is structured so the simulated governance step can be replaced with a live node attestation via env vars
          (<code>KATLAS_MODE=live</code>, <code>KATLAS_NODE_URL=…</code>). The header chip on every page reflects the current state in real time
          ({live ? "node active" : "simulation"}). The KATLAS node/API integration pattern has been wired against a separate <code>node-api-setup</code> reference deployment;
          when a real attestation endpoint is provisioned on that node, ClubCierge receipts automatically carry a live attestation id with no further code changes.
        </div>
      </Chapter>

      {/* Comparison panel */}
      <section className="space-y-3" data-testid="orchestration-comparison">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Comparison</div>
          <h2 className="text-2xl font-semibold mt-1">Why this is not just orchestration</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">A normal workflow app routes tasks. KATLAS-connected workflows attest them.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="p-5 border-border race-card">
            <div className="tracking-eyebrow text-muted-foreground">Normal workflow app</div>
            <ul className="text-[13px] mt-2 space-y-1.5 text-muted-foreground">
              <li>Routes tasks</li>
              <li>Stores data centrally</li>
              <li>Relies on users to trust records</li>
              <li>May let AI see too much context</li>
              <li>Weak evidence of who authorised what</li>
            </ul>
          </Card>
          <Card className="p-5 border-border race-card bg-white">
            <div className="tracking-eyebrow text-[#26262E]">ClubCierge demo</div>
            <ul className="text-[13px] mt-2 space-y-1.5">
              <li>Shows AI-assisted workflow</li>
              <li>Simulates KATLAS governance for demo clarity</li>
              <li>Shows shared / withheld data</li>
              <li>Produces local receipts</li>
              <li>Demonstrates the user experience</li>
            </ul>
          </Card>
          <Card className="p-5 border-border race-card bg-[#FFF7F2] border-[#F25E1D]/40">
            <div className="tracking-eyebrow text-[#F25E1D] inline-flex items-center gap-1"><Network className="h-3 w-3" />KATLAS-connected workflow</div>
            <ul className="text-[13px] mt-2 space-y-1.5">
              <li>Role / wallet-backed authority</li>
              <li>Live node attestation</li>
              <li>Minimal governance payload</li>
              <li>No raw private data or withheld assets sent to node</li>
              <li>Receipt proves who approved what, under what role, and what was shared or withheld</li>
            </ul>
          </Card>
        </div>
      </section>

      {/* Evidence visibility matrix — shared component (also used by satellites) */}
      <EvidenceMatrix
        subtitle="Who controls each piece of evidence, who can see it, what reaches the KATLAS node, and what appears on the public share view. KATLAS records the governed action — not a copy of everyone's private data."
      />

      {/* Flow diagram */}
      <section className="space-y-3" data-testid="flow-diagram">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Operating model</div>
          <h2 className="text-2xl font-semibold mt-1">The governance flow</h2>
        </div>
        <Card className="p-6 border-border race-card">
          <div className="flex flex-col items-center gap-1.5 sm:gap-2">
            <FlowStep icon={Wallet} label="Member / Driver Wallet" />
            <ArrowDown className="h-4 w-4 text-muted-foreground" />
            <FlowStep icon={Sparkles} label="AI-assisted match or offer suggestion" />
            <ArrowDown className="h-4 w-4 text-muted-foreground" />
            <FlowStep icon={Layers} label="Policy gate — role + permission check" />
            <ArrowDown className="h-4 w-4 text-muted-foreground" />
            <FlowStep icon={UserRound} label="Human approval — officer / parent / authorised role" />
            <ArrowDown className="h-4 w-4 text-muted-foreground" />
            <FlowStep icon={Network} label={live ? "KATLAS node active" : "KATLAS governance simulation"} accent />
            <ArrowDown className="h-4 w-4 text-muted-foreground" />
            <FlowStep icon={FileCheck2} label="Receipt — shared / withheld / approved / timestamp" />
          </div>
        </Card>
      </section>

      <Card className="p-5 border-border bg-[#FAFAFB] race-card">
        <div className="text-sm leading-relaxed">
          <b>Plain-English close.</b> Self-sovereign profiles let the right role prove the right fact without exposing
          everything. KATLAS records who approved what, under what role, and what was shared or withheld — without
          needing a public copy of the underlying private data.
        </div>
      </Card>
    </div>
  );
}
